import styled, { keyframes } from 'styled-components'
import { sureco } from '../shared/finance.styles'

/* Teal tint for selected states; sureco has no light teal. */
const TEAL_TINT = '#E6F6F7'

export const FilterTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 640px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    flex-shrink: 0;
    width: 100%;
    padding-bottom: 2px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

export const FilterTab = styled.button<{ $active?: boolean }>`
  height: 34px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? sureco.teal : sureco.borderStrong)};
  background: ${({ $active }) => ($active ? TEAL_TINT : sureco.white)};
  color: ${({ $active }) => ($active ? sureco.tealDark : sureco.fg2)};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: ${sureco.teal};
    color: ${sureco.tealDark};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${sureco.focusRing};
  }
`

export const MatchFilterTab = styled.button<{
  $active?: boolean
  $variant?: 'unmatched' | 'matched'
}>`
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  border: 1px solid
    ${({ $active, $variant }) => {
      if (!$active) return sureco.border
      return $variant === 'unmatched' ? '#fca5a5' : '#86efac'
    }};
  background: ${({ $active, $variant }) => {
    if (!$active) return sureco.white
    return $variant === 'unmatched' ? '#fee2e2' : '#dcfce7'
  }};
  color: ${({ $active, $variant }) => {
    if (!$active) return sureco.fg2
    return $variant === 'unmatched' ? '#991b1b' : '#166534'
  }};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: ${({ $variant }) =>
      $variant === 'unmatched' ? '#fca5a5' : '#86efac'};
    color: ${({ $variant }) =>
      $variant === 'unmatched' ? '#991b1b' : '#166534'};
    background: ${({ $variant }) =>
      $variant === 'unmatched' ? '#fee2e2' : '#dcfce7'};
  }
`

export const MatchFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 16px 8px;
  border-top: 1px solid ${sureco.borderSoft};
  background: ${sureco.offWhite};
  border-radius: 0 0 12px 12px;
`

export const MatchFilterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${sureco.fg3};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 4px;
`

/* Plain toolbar like the billing profile page: search, filters, count. */
export const SearchRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .ant-input-affix-wrapper {
    border-radius: 6px;
    border-color: ${sureco.borderStrong};
  }
`

export const SearchDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${sureco.border};
  flex-shrink: 0;
  margin: 0 6px;

  @media (max-width: 640px) {
    display: none;
  }
`

export const ResultsCount = styled.span`
  margin-left: auto;
  font-size: 13px;
  color: ${sureco.fg3};
  white-space: nowrap;

  strong {
    font-weight: 600;
    color: ${sureco.fg1};
  }
`

/* Mirrors the billing profile table card. */
export const TableCard = styled.div`
  background: ${sureco.white};
  border-radius: 12px;
  border: 1px solid ${sureco.border};
  box-shadow: ${sureco.shadowSm};
  overflow: auto;

  /*
   * Use !important on cell padding to beat AntD v5's CSS-in-JS selector
   * \`.css-XXX .ant-table.ant-table-small .ant-table-tbody > tr > td\`
   * which would otherwise override our rule and default to 8px padding.
   * Keeping padding consistent between parent rows and the expanded-row
   * grid cells is what guarantees left-edge alignment of cell content.
   */
  && .ant-table-thead > tr > th {
    padding: 0 12px !important;
    height: 48px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: ${sureco.fg1};
    background: ${sureco.offWhite};
    border-bottom: 1px solid ${sureco.border};
    text-align: left;
    white-space: nowrap;
  }

  /* antd's column separator, drawn as the design's short divider. */
  && .ant-table-thead > tr > th:not(:last-child)::before {
    background-color: ${sureco.borderStrong} !important;
    width: 2px !important;
    height: 14px !important;
  }

  && .ant-table-tbody > tr > td {
    padding: 10px 12px !important;
    font-size: 14px;
    color: ${sureco.fg1};
    vertical-align: middle;
    text-align: left;
    border-bottom: 1px solid ${sureco.border};
  }

  && .ant-table-tbody > tr.ant-table-row-selected > td {
    background: ${TEAL_TINT};
  }

  /* Selection + expand columns are ornamental — give them tighter,
     symmetric padding so the checkbox/icon centers within the column
     rather than inheriting the 12px horizontal gutter used for data
     columns (which would push them off-center against their column rule).
     The !important on text-align overrides the .ant-table-tbody > tr > td
     rule above (which is more specific by element-chain than ours). */
  && .ant-table-thead > tr > th.ant-table-selection-column,
  && .ant-table-tbody > tr > td.ant-table-selection-column,
  && .ant-table-thead > tr > th.ant-table-row-expand-icon-cell,
  && .ant-table-tbody > tr > td.ant-table-row-expand-icon-cell {
    padding-left: 0 !important;
    padding-right: 0 !important;
    text-align: center !important;
  }

  && .ant-table-tbody > tr:hover > td {
    background: ${sureco.offWhite};
  }

  /* Expanded-row cell spans all columns; zero its padding so the nested
     grid can start at x=0 and share the same column edges as parent rows. */
  && .ant-table-expanded-row > td.ant-table-cell {
    padding: 0 !important;
  }

  && .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 14px 20px;
    border-top: 1px solid ${sureco.border};
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Push "1–25 of N" to the left edge so page controls + size changer
     group cleanly on the right. */
  && .ant-pagination-total-text {
    margin-right: auto;
    font-size: 13px;
    color: ${sureco.fg3};
  }
`

/*
 * Delivery tables only: Company (third column, after selection and expand)
 * stays frozen at the left edge, so each row keeps its company in view while
 * the other columns scroll.
 */
export const DeliveryTableCard = styled(TableCard)`
  && .ant-table-thead > tr > th:nth-child(3),
  && .ant-table-tbody > tr.ant-table-row > td:nth-child(3) {
    position: sticky;
    left: 0;
    z-index: 2;
    box-shadow: 8px 0 8px -8px rgba(17, 24, 39, 0.18);
  }

  &&
    .ant-table-tbody
    > tr.ant-table-row:not(:hover):not(.ant-table-row-selected)
    > td:nth-child(3) {
    background: ${sureco.white};
  }

  /*
   * Values and "—" dashes are centred in every column except Company (3) and
   * File (5), which read as text. History rows share the column order, so
   * their grid cells are centred the same way to stay aligned.
   */
  && .ant-table-thead > tr > th:nth-child(4),
  && .ant-table-thead > tr > th:nth-child(n + 6),
  && .ant-table-tbody > tr.ant-table-row > td:nth-child(4),
  && .ant-table-tbody > tr.ant-table-row > td:nth-child(n + 6),
  && [data-testid='history-row'] > div:nth-child(4),
  && [data-testid='history-row'] > div:nth-child(n + 6) {
    text-align: center !important;
  }

  && .ant-table-thead > tr > th:nth-child(n + 6) .ant-table-column-sorters {
    justify-content: center;
    gap: 4px;
  }

  && .ant-table-thead > tr > th:nth-child(n + 6) .ant-table-column-title {
    flex: 0 1 auto;
  }

  && .ant-table-tbody > tr.ant-table-row > td:nth-child(n + 6) > div,
  && [data-testid='history-row'] > div:nth-child(n + 6) > div {
    justify-content: center;
    align-items: center;
  }
`

export const HistoryLinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${sureco.teal};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover:not(:disabled) {
    color: ${sureco.tealDark};
    text-decoration: underline;
  }

  &:disabled {
    color: ${sureco.fg3};
    cursor: not-allowed;
  }
`

const slideUp = keyframes`
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
`

export const FloatingBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  /* A fixed box at left: 50% would otherwise shrink to half the viewport and wrap its buttons. */
  width: max-content;
  max-width: calc(100vw - 48px);
  background: ${sureco.fg1};
  color: ${sureco.white};
  border-radius: 12px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 100;
  animation: ${slideUp} 0.25s ease-out;
`

export const FloatingBarCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

export const FloatingBarButton = styled.button<{
  $variant?: 'approve' | 'exclude' | 'delete'
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition:
    background 0.15s,
    opacity 0.15s;

  background: ${({ $variant }) =>
    $variant === 'exclude' || $variant === 'delete' ? '#ef4444' : '#16a34a'};
  color: #fff;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const FloatingBarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
`

export const DeliveryRatio = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  color: ${sureco.fg3};
`

export const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`
